import { IIndicator, IInsight, IInsightDialog, MessageTemplating } from '@jumpstart/core';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import Grid from '@mui/material/Grid';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemText from '@mui/material/ListItemText';
import Snackbar from '@mui/material/Snackbar';
import Typography from '@mui/material/Typography';
import { nanoid } from 'nanoid';
import objectPath from 'object-path';
import React, { useEffect, useRef, useState } from 'react';
import { DialogUtils } from '../dialog-utils';
import { formStyles } from '../styles';
import ContentField from './ContentField';
import DialogEditor from './DialogEditor';
import DialogTreeVisual, { EnhancedInsightDialog, InsightNode } from './DialogTreeVisual';
import { testDog, testProfile } from './utils';


export interface IDialogTree {
  indicator: IIndicator;
  event: IInsight;
  loading: boolean;

  onChange(newIndicator: IIndicator, dialog?: IInsightDialog): void;
}

class DialogCRUD {

  public static remove(indicator: IIndicator, dialogPath: string): IIndicator {
    const newIndicator = { ...indicator };
    objectPath.del(newIndicator, dialogPath);
    return newIndicator;
  }

  public static set(indicator: IIndicator, dialogPath: string, dialog: IInsightDialog): IIndicator {
    const newIndicator = { ...indicator };
    objectPath.set(newIndicator, dialogPath, dialog);
    return newIndicator;
  }
}

export default function DialogTree(props: IDialogTree) {
  const { indicator, event, onChange, loading } = props;
  const [node, setNode] = useState<InsightNode | null>(null);
  const [size, setSize] = useState<any>(null);
  const [staged, setStaged] = useState<{ existing: string | null; responsePath: string; next: string | null; question?: string | null; node: InsightNode | null } | null>(
    null);
  const cref = useRef<any>(null);
  const [error, setError] = useState<Error | null>(null);


  const handleChangedDialog = (path: string, dialog: IInsightDialog) => {
    const newIndicator = { ...indicator };
    objectPath.set(newIndicator, path, dialog);
    onChange(newIndicator, dialog);
  };

  const handleRequestUpdate = (nodes: InsightNode[]) => {
    const newIndicator = { ...indicator };
    const isUnique = (node: InsightNode) => {
      const _nodes = nodes.filter((n) => n.data.id === node.data.id)
      return _nodes.length === 1;
    }
    for (const node of nodes) {
      if (!isUnique(node)) {
        console.log('not unique');
        const id = nanoid(10);
        objectPath.set(newIndicator, node.data.parent, { ...node.data, id });
      } else {
        objectPath.set(newIndicator, node.data.parent, node.data);
      }
    }
    onChange(newIndicator);
    // Must refresh the page
    setTimeout(() => {
      window.location.reload();
    }, 1200);
  }

  const handleOpenNode = (node: InsightNode) => {
    console.log(node.data.parent);
    setNode(node);
  };

  const handleAddNode = (node: InsightNode) => {
    // Open dialog to get response key
    console.log('Add Answer');
    setStaged({
      existing: null,
      responsePath: `${node.data.parent}.responses`,
      next: null,
      question: MessageTemplating.process(node.data.question ? node.data.question : 'N/A', testDog, testProfile),
      node: null
    });
  };

  const getPastedNode = (target: InsightNode, copy: InsightNode): EnhancedInsightDialog | null => {
    if (!target.data.answer) {
      console.error('No answer (no parent)');
      return null;
    }
    const { parent, answer, responses, ...rest } = copy.data;
    return {
      ...rest,
      id: target.data.id ? target.data.id : nanoid(10),
      parent: target.data.parent,
      answer: target.data.answer
    };
  };

  const handleSoftPasteNode = (target: InsightNode, copy: InsightNode) => {
    // Paste a node by keeping its existing child relationships
    const updatedDialog = getPastedNode(target, copy);
    if (updatedDialog) {
      let newIndicator = { ...indicator };
      const nextPath: string = `${target.data.parent}`;
      newIndicator = DialogCRUD.set(newIndicator, nextPath, updatedDialog);
      onChange(newIndicator, updatedDialog);
    }
  };

  const handlePasteInstructionsNode = (target: InsightNode, copy: InsightNode) => {
    target.data.highlights = copy.data.highlights;
    target.data.tips = copy.data.tips;
    target.data.reminders = copy.data.reminders;
    let newIndicator = { ...indicator };
    const nextPath: string = `${target.data.parent}`;
    newIndicator = DialogCRUD.set(newIndicator, nextPath, target.data);
    onChange(newIndicator, target.data);
  }

  const handlePasteNode = (target: InsightNode, copy: InsightNode) => {
    // Paste a node, replacing its existing child relationships
    if (!copy.data.parent) {
      setError(new Error('Cannot copy a root node'));
      return null;
    }
    const { parent, answer, responses, ...rest } = copy.data;
    const updatedDialog = getPastedNode(target, copy);
    if (updatedDialog) {
      updatedDialog.responses = { ...DialogUtils.handleNewIds(DialogUtils.parent(target), responses) };
      console.log("CRAWL")
      let newIndicator = { ...indicator };
      const nextPath: string = `${target.data.parent}`;
      newIndicator = DialogCRUD.set(newIndicator, nextPath, updatedDialog);
      onChange(newIndicator, updatedDialog);
    }
  };

  const handleAssignNode = (node: InsightNode, assignee: InsightNode) => {
    // Open dialog to get response key
    console.log('Add Answer');
    assignee.data.parent = `${node.data.parent}.responses.${assignee.data.response}`;
    const { responses, parent, answer, ...rest } = assignee.data;
    const updatedDialog = {
      ...rest,
      id: node.data.id ? node.data.id : nanoid(10),
      responses: DialogUtils.handleNewIds(DialogUtils.parent(node), node.data.responses),
      parent: `${node.data.parent}.responses.${assignee.data.response}`,
      answer: node.data.answer
    };
    assignee.data = updatedDialog;
    setStaged({
      existing: null,
      responsePath: `${node.data.parent}.responses`,
      next: null,
      question: MessageTemplating.process(node.data.question ? node.data.question : 'N/A', testDog, testProfile),
      node: assignee
    });
  };

  const handleEditAnswer = (node: InsightNode) => {
    console.log('Edit Answer');
    setStaged({
      existing: node.data.answer,
      responsePath: `${node.parent ? node.parent.data.parent : ''}.responses`,
      next: null,
      question: MessageTemplating.process(
        node.parent && node.parent.data.question ? node.parent.data.question : 'N/A',
        testDog,
        testProfile
      ),
      node: null
    });
  };

  const handleRemoveNode = (node: InsightNode) => {
    console.log(node.data.parent);
    const newIndicator = DialogCRUD.remove(indicator, node.data.parent);
    onChange(newIndicator, { ...node.data, id: node.data.id ? node.data.id : nanoid(10) });
  };

  const handleSubmitStaged = () => {
    if (!staged) {
      console.error('Nothing staged');
      return;
    }
    // responsePath = path to response key
    const { existing, responsePath, next, node } = staged;
    console.log(staged);
    if (!next) {
      console.error('No next');
      setStaged(null);
      return;
    }
    let newIndicator = { ...indicator };
    const nextPath: string = `${responsePath}.${next.replace(/\./g, '')}`;
    if (existing) {
      const existingPath = `${responsePath}.${existing}`;
      const copied = objectPath.get(indicator, existingPath);
      if (copied) {
        newIndicator = DialogCRUD.remove(newIndicator, existingPath);
        newIndicator = DialogCRUD.set(newIndicator, nextPath, copied);
      } else {
        console.error('Could not copy path');
        return;
      }
    } else {
      if (node) {
        newIndicator = DialogCRUD.set(newIndicator, nextPath, node.data);
      } else {
        const newDialog: IInsightDialog = {
          id: nanoid(10),
          body: '',
          responses: {}
        };
        newIndicator = DialogCRUD.set(newIndicator, nextPath, newDialog);
      }
    }
    setStaged(null);
    onChange(newIndicator);
  };

  useEffect(() => {
    // Calculate size of tree based on parent view port
    if (cref.current) {
      setSize({ width: cref.current.offsetWidth, height: cref.current.offsetHeight });
    } else {
      setSize(null);
    }
  }, [cref.current]);

  return (
    <Grid container direction="column" ref={cref} style={{ width: '100%', height: '100%', minHeight: '80vh' }}>
      <Snackbar
        open={!!error}
        autoHideDuration={6000}
        onClose={() => {
          setError(null);
        }}
        message={error ? error.message : null}
      />
      {size && <DialogTreeVisual
        indicator={indicator}
        size={size}
        onRequestUpdate={handleRequestUpdate}
        onPasteInstructions={handlePasteInstructionsNode}
        onPaste={handlePasteNode}
        onPasteSoft={handleSoftPasteNode}
        onOpen={handleOpenNode}
        onAdd={handleAddNode}
        onAssign={handleAssignNode}
        onRemove={handleRemoveNode}
        onEditAnswer={handleEditAnswer}
      />}
      {node && <DialogEditor
        nodeProps={[node, setNode]}
        event={event}
        indicator={indicator}
        dialog={node.data}
        path={node.data.parent}
        loading={loading}
        onError={setError}
        onSubmit={(path: string, dialog: IInsightDialog) => handleChangedDialog(path, dialog)}
        question={node.parent && node.parent.data.question ? node.parent.data.question : null}
        action={node.parent && node.parent.data.body ? node.parent.data.body : null}
        answer={node.data.answer}
      />}
      {/*<Dialog*/}
      {/*  open={!!node}*/}
      {/*  fullScreen*/}
      {/*  onClose={() => setNode(null)}*/}
      {/*>*/}
      {/*  <DialogActions*/}
      {/*    style={{ width: '100%', position: 'absolute' }}*/}
      {/*  >*/}
      {/*    <IconButton onClick={() => setNode(null)}><CloseIcon/></IconButton>*/}
      {/*  </DialogActions>*/}
      {/*  <DialogTitle>Edit Dialog</DialogTitle>*/}
      {/*  <DialogContent>*/}
      {/*    <Grid container justifyContent="center">*/}
      {/*      */}
      {/*    </Grid>*/}
      {/*  </DialogContent>*/}
      {/*</Dialog>*/}

      <Dialog
        open={!!staged}
        fullWidth
        maxWidth="sm"
        onClose={() => setStaged(null)}
      >
        <DialogTitle>{staged && staged.existing ? 'Edit' : 'Add'} Answer</DialogTitle>
        <DialogContent>
          <Typography
            variant="body1"
            style={{ marginBottom: 24 }}><b>Question</b>: {staged && staged.question ? staged.question : 'N/A'}
          </Typography>
          {
            staged ?
              <ContentField
                id="answer"
                name="answer"
                defaultValue={staged.existing}
                onBlur={(event: React.FocusEvent<HTMLInputElement>) => {
                  console.log('New staged', { ...staged, next: event.target.value });
                  setStaged({ ...staged, next: event.target.value });
                }}
                sx={formStyles.input}
                style={{ width: '100%' }}
                variant="outlined"
                label="Answer"
                placeholder="Answer"
                multiline
                InputLabelProps={{ shrink: true }}
                helperText={`Parent Path: ${staged ? staged.responsePath : null}`}
                validation={{}}
                touched={true}
              />
              : null
          }
          {staged && !staged.existing && staged.node &&
          <Grid container direction="column" style={{ padding: 24 }}>
            <Typography
              variant="body2"
              style={{ marginBottom: 12 }}
            >
              Assigning Existing Node
            </Typography>
            <List>
              <ListItem>
                <ListItemText
                  primary={
                    <Grid container direction="column">
                      <Typography variant="caption">Action: {staged.node.data.body}</Typography>
                      <Typography variant="caption">Question: ${staged.node.data.question}</Typography>
                    </Grid>
                  }
                />
              </ListItem>
            </List>
          </Grid>
          }
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setStaged(null);
            }}
            color="info"
            variant="contained"
          >
            Cancel
          </Button>
          <Button onClick={handleSubmitStaged} color="primary" variant="contained">
            Submit
          </Button>
        </DialogActions>
      </Dialog>

    </Grid>
  );
}
